<!--
  The product page's three drawings.

  unomena.com's `Sketch.svelte` is the model, not the source: same viewBox,
  one `currentColor` stroke at 2.4, the same feTurbulence/feDisplacementMap
  pair for the pencil wobble, and exactly one crisp red full stop per drawing.
  The pictures themselves are new, because the site's are about the studio and
  these are about the document.

  They are one set, told in order: the same sheet of paper three times.

    write    an agent plugged into the page, writing lines onto it
    comment  a phrase bracketed, pinned, and answered in the margin
    answer   the reply landing in the gap, under the words it belongs to

  The filter id carries the kind, because two `<filter id="sk">` in one
  document is one filter, and the second drawing would be displaced by the
  first one's turbulence. The seed differs per kind for the same reason a set
  of drawings is not one drawing traced three times.

  `aria-hidden` on the whole thing: it is a picture of what the prose beside
  it already says, and the prose is the accessible version.
-->
<script lang="ts">
  type Kind = 'write' | 'comment' | 'answer'

  let { kind }: { kind: Kind } = $props()

  /** A different wobble per drawing, so the set is drawn rather than stamped. */
  const SEED: Record<Kind, number> = { write: 3, comment: 11, answer: 7 }
</script>

<svg
  class="sketch"
  viewBox="0 0 240 180"
  fill="none"
  stroke="currentColor"
  stroke-width="2.4"
  stroke-linecap="round"
  stroke-linejoin="round"
  aria-hidden="true"
  focusable="false"
>
  <defs>
    <filter id="lsk-{kind}" x="-6%" y="-6%" width="112%" height="112%">
      <feTurbulence
        type="fractalNoise"
        baseFrequency="0.015"
        numOctaves="2"
        seed={SEED[kind]}
        result="n"
      />
      <feDisplacementMap
        in="SourceGraphic"
        in2="n"
        scale="2.4"
        xChannelSelector="R"
        yChannelSelector="G"
      />
    </filter>
  </defs>

  {#if kind === 'write'}
    <!-- A sheet of paper with an agent plugged into its edge, four lines
         written and the fifth still coming. The red dot is the full stop it
         just put down. -->
    <g filter="url(#lsk-{kind})">
      <path d="M98 18 H194 V162 H98 Z" />
      <path d="M114 52 H178" />
      <path d="M114 72 H166" />
      <path d="M114 92 H178" />
      <path d="M114 112 H150" />
      <!-- the socket, cut into the left edge -->
      <path d="M98 78 H84 V106 H98" />
      <!-- the plug, its two pins, and the lead running off the page -->
      <path d="M84 84 H62 V100 H84 Z" />
      <path d="M84 88 H92" />
      <path d="M84 96 H92" />
      <path d="M62 92 C44 92 38 116 30 122" />
    </g>
    <circle cx="158" cy="112" r="4.6" class="dot" />
  {:else if kind === 'comment'}
    <!-- The same sheet: one phrase boxed, a leader out to a note in the
         margin. The red dot is the anchor — the exact place the note is
         pinned to, which is the whole idea. -->
    <g filter="url(#lsk-{kind})">
      <path d="M44 18 H140 V162 H44 Z" />
      <path d="M60 48 H124" />
      <!-- the phrase, boxed where the selection was made -->
      <path d="M56 62 H114 V84 H56 Z" />
      <path d="M64 73 H104" />
      <path d="M60 124 H124" />
      <path d="M60 144 H96" />
      <!-- out to the note in the margin -->
      <path d="M128 73 H144" />
      <path d="M144 54 H196 V92 H144 Z" />
      <path d="M154 68 H186" />
      <path d="M154 80 H174" />
    </g>
    <circle cx="122" cy="73" r="4.6" class="dot" />
  {:else}
    <!-- The same sheet again: the answer has opened the page between two
         paragraphs, its tail reaching back up to the words it answers. The
         red dot is that spot. -->
    <g filter="url(#lsk-{kind})">
      <path d="M72 18 H168 V162 H72 Z" />
      <path d="M88 42 H152" />
      <path d="M88 60 H136" />
      <!-- the answer, opened between two paragraphs, its tail reaching back
           up to the words it answers -->
      <path d="M84 76 H156 V116 H84 Z" />
      <path d="M122 76 L140 64 L132 76" />
      <path d="M94 90 H146" />
      <path d="M94 104 H124" />
      <path d="M88 132 H152" />
      <path d="M88 150 H124" />
    </g>
    <circle cx="144" cy="60" r="4.6" class="dot" />
  {/if}
</svg>

<style>
  .sketch {
    display: block;
    width: 100%;
    /* The frame around it owns the height (`.shot`, aspect-ratio 4/3); the
       drawing keeps its own proportions inside it and is centred there. */
    height: auto;
  }

  /* The one constant across all three, and the only colour in any of them.
     It never inherits: the stroke is ink, the full stop is red. */
  .dot {
    fill: var(--color-red);
    stroke: none;
  }

  /*
   * TASK-047, and copied from ../unomena-website/src/lib/components/Sketch.svelte
   * line for line. Each drawing's red dot is a station on the traveling dot's
   * itinerary: the system hollows it (`.tdot-hollow`) and the ONE dot it flies
   * around docks there, so the drawing is filled by the visitor rather than
   * printed with a full stop of its own. `:global()` because the class is
   * added at runtime by TravelingDot.svelte, which the compiler cannot see.
   *
   * Without the system -- touch, reduced motion, the chunk never fetched --
   * the class is never added and the dot above stays exactly as it is.
   */
  .dot:global(.tdot-hollow) {
    fill: none;
    stroke: none;
  }
</style>
